import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IAcquisition } from '@models/acquisition';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { IDecayEstimate } from '@models/decay/decay-estimate';
import { ILocalStorage } from '@models/localStorage';
import { IReaggregationHistoryItem } from '@models/Reaggregation/reaggregation-history-item';
import { IGlobalSetting } from '@models/settings/global-setting';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { DecayBalanceService } from '@services/decay/decay-balance.service';
import { DecayCalibrationTypeService } from '@services/decay/decay-calibration-type.service';
import { DecayCalibrationService } from '@services/decay/decay-calibration.service';
import { AcquisitionService } from '@services/global/acquisition.service';
import { GlobalSettingsService } from '@services/global/global-settings.service';
import { ProductReaggregationService } from '@services/global/product-reaggregation.service';
import { ImportService } from '@services/import/import.service';
import { LocalStorageService } from '@services/local-storage.service';
import { clone } from '@shared/arrays';
import { getDateString } from '@shared/dates';
import { setEstimateColours } from '@shared/decay-helpers';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ObservableStore } from '../observable-store/dist';

declare var require: any;
const uuidv4 = require('uuid/v4');

const EMPTY_ESTIMATE: IDecayEstimate = {
  DecayCalibrationId: null,
  Curtailment: null,
  DecayEstimateSettingId: null,
  DecayResult: {
    DecayCoefficients: [],
    DecayEstimateResults: [],
    DecayResultId: -1,
    DegreeFreedom: 0,
    FStat: 0,
    RSquared: 0,
    StandardError: 0,
    DecayEstimateSettingId: -1
  },
  EndObservation: null,
  FunctionalForm: null,
  IsChosen: false,
  StartObservation: null,
  Id: '',
  StartDate: null,
  ExcludeAcquisitionIds: [],
  ProductId: null,
  Name: 'New Estimate',
  Colour: '#AAAAAA',
  NeedsReRun: false,
  DateTimeCreated: null,
  Errors: [],
  DecayCalibrationType: 1
};

@Injectable({ providedIn: 'root' })
export class DecayEstimateService extends ObservableStore<IStoreState> {
  public estimatesFetchedFromLocalStorage = false;
  public gotEstimatesFromDatabase = false;
  public observationLimit: number = null;
  public usedColours = [];

  private estimateApiURL: string;
  private lastImportDate: Date;
  private productReaggregationHistory: IReaggregationHistoryItem;
  private readonly estimates$: BehaviorSubject<IDecayEstimate[]> = new BehaviorSubject<IDecayEstimate[]>([clone(EMPTY_ESTIMATE)]);
  // private readonly globalEstimateChange$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly selectedEstimateIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private selectedDecayCalibration: IDecayCalibration;

  get estimates() {
    return this.estimates$.value;
  }

  set estimates(estimates: IDecayEstimate[]) {
    estimates = setEstimateColours(estimates);
    this.estimates$.next(estimates);
  }

  get estimatesListener() {
    return this.estimates$.asObservable();
  }

  get selectedEstimateIndex() {
    return this.selectedEstimateIndex$.value;
  }

  set selectedEstimateIndex(selectedEstimate: number) {
    if (selectedEstimate >= 0 && selectedEstimate <= this.estimates.length - 1) {
      this.selectedEstimateIndex$.next(selectedEstimate);
    }
  }

  get selectedEstimateListener() {
    return this.selectedEstimateIndex$.asObservable();
  }

  public globalChange = false;

  constructor(
    private readonly acquisitionService: AcquisitionService,
    private readonly appConfig: AppConfigProvider,
    private readonly decayService: DecayBalanceService,
    private readonly http: HttpClient,
    private readonly importService: ImportService,
    private readonly localStorageService: LocalStorageService,
    private readonly globalSettingsService: GlobalSettingsService,
    private readonly productReaggregationService: ProductReaggregationService,
    private readonly decayCalibrationService: DecayCalibrationService,
    private readonly decayCalibrationTypeService: DecayCalibrationTypeService
  ) {
    super({ trackStateHistory: false });
    const estimates: IDecayEstimate[] = [];
    this.setState({ estimates }, StoreActions.SetEstimates);

    this.appConfig.environment.subscribe(env => {
      this.estimateApiURL = `${env.API.DECAY}/api/decay/calibration/estimate`;
    });
  }

  /**
   * Deletes a specific estimate from the store
   * @param estimateId The id of the estimate that needs to be deleted  e.g. estimate.Id
   * @return deletedEstimate The estimate that was deleted
   */
  public deleteEstimate(estimateId: string, productId = null): IDecayEstimate {
    const estimates = clone(this.estimates);
    const deletedEstimate = estimates.find(({ Id }) => Id === estimateId);
    const updatedEstimates = this.setEstimatesNames(estimates.filter(({ Id }) => Id !== estimateId));

    this.selectedEstimateIndex += -1;

    this.estimates = updatedEstimates;
    // Ensuring we can store these estimates
    const estimatesToStore = updatedEstimates.filter(({ Id }) => Id);
    this.usedColours = this.usedColours.filter(colour => colour !== deletedEstimate.Colour);
    productId = deletedEstimate.ProductId || updatedEstimates[0].ProductId || productId;

    if (!productId) {
      console.warn(
        'wrong productId (Decay-LocalStorage) while deleting estimate',
        { estimateId, deletedEstimate },
        {
          productId,
          updatedEstimates,
          estimatesToStore,
          deletedEstimate
        }
      );
      throw new Error(`Error deleting estimate`);
    } else {
      if (estimatesToStore.length > 0) {
        this.storeInLocalStorage(updatedEstimates, productId);
      } else {
        // Despite the fact that we aren't changing the local storage we still need to mark the storage as no
        // longer being pristine
        this.storeInLocalStorage([], productId);

        // We don't want to save anything if there are no estimates to save
        this.decayService.canDeactivate = updatedEstimates.length === 0;
      }
    }
    return deletedEstimate;
  }

  public addOrUpdateEstimate(estimate: IDecayEstimate) {
    let isNewEstimate = false;

    if (!estimate.Id) {
      estimate.Id = uuidv4();
      isNewEstimate = true;
    }

    const {
      FunctionalForm,
      ProductId,
      StartObservation,
      EndObservation,
      Curtailment,
      StartDate,
      ExcludeAcquisitionIds,
      DecayCalibrationType
    } = estimate;

    const query = {
      FunctionalForm,
      ProductId,
      StartObservation,
      EndObservation,
      Curtailment,
      StartDate: getDateString(StartDate),
      ExcludeAcquisitionIds,
      DecayCalibrationType
    };

    if (!query.ExcludeAcquisitionIds) {
      delete query.ExcludeAcquisitionIds;
    }

    return this.http.post<IDecayEstimate>(this.estimateApiURL, query).pipe(
      map((updatedEstimate: IDecayEstimate) => {
        updatedEstimate.IsChosen = estimate.IsChosen;
        updatedEstimate.Id = estimate.Id;
        updatedEstimate.IsValid = true;
        // Date time stuff god forgive me
        updatedEstimate.DateTimeCreated = new Date().toISOString();
        updatedEstimate.DateTimeCreated = updatedEstimate.DateTimeCreated.substr(0, updatedEstimate.DateTimeCreated.length - 1);

        if (updatedEstimate.DecayResult.DecayCoefficients === null) {
          updatedEstimate.DecayResult.DecayCoefficients = [];
        }

        updatedEstimate.DecayCalibrationType = query.DecayCalibrationType;
        const estimates: IDecayEstimate[] = clone(this.estimates);
        let est: IDecayEstimate;

        if (isNewEstimate) {
          est = updatedEstimate;
          estimates[estimates.length - 1] = est;
          this.cleanupEstimates(estimates);
        } else {
          const estimateIndex = estimates.findIndex(e => e.Id === estimate.Id);
          let existingItem = {};
          if (estimateIndex >= 0) {
            existingItem = estimates[estimateIndex];
            est = { ...existingItem, ...updatedEstimate };
            est.IsChosen = false;
            est.NeedsReRun = false;
            estimates[estimateIndex] = est;
          } else {
            est = updatedEstimate;
            estimates.push(est);
          }
        }

        setEstimateColours(estimates);
        this.estimates = estimates;
        if (isNewEstimate) {
          this.selectedEstimateIndex = this.estimates.findIndex(({ Id }) => Id === est.Id);
        }
        this.storeInLocalStorage(estimates, query.ProductId);

        return updatedEstimate;
      }),
      catchError(err => {
        // If there is an error we need to ensure the estimate list is intact
        const newEstimate = this.estimates.find(est => EMPTY_ESTIMATE.Name === est.Name);
        if (newEstimate.Id) {
          // Estimate does not have an ID
          this.estimates.splice(this.estimates.length - 1, 1);
          this.estimates = this.cleanupEstimates(this.estimates);
        }
        // Still need to throw the error to alert the front end that the error has occurred
        throw new Error(err.toString());
      })
    );
  }

  /**
   *  * Sort: Chosen estimate shown 1st
   *  * Set estimate Ids
   *  * Set estimate Names
   *  * Add 'New Estimate' element
   */
  public cleanupEstimates(estimates: IDecayEstimate[]) {
    estimates = this.sortEstimates(estimates);
    estimates = this.setEstimatesIds(estimates);
    estimates = this.setNewEstimate(estimates);
    estimates = this.setEstimatesNames(estimates);
    return estimates;
  }

  /**
   * Will return the saved decay data makes the comparison between the local storage data
   * as well as the data stored on the DB and returns the most recent one if present
   * if no data is available creates a estimates
   */
  public async getSavedData(productId: number): Promise<IDecayCalibration> {
    const results: [
      IDecayCalibration,
      ILocalStorage<IDecayCalibration>,
      IAcquisition[],
      Date,
      IReaggregationHistoryItem,
      IGlobalSetting
    ] = await forkJoin([
      this.decayCalibrationService.getLatestCalibrationForProduct(productId),
      of(this.localStorageService.fetchFromLocalStorage<IDecayCalibration>(LocalStorageKeys.Decays, productId)),
      this.acquisitionService.fetchAcquisitions(),
      this.importService.getLastImportDate(),
      this.productReaggregationService.getProductReaggregationHistory(productId),
      this.globalSettingsService.getSettingByName('OBSERVATION_LIMIT'),
      this.decayCalibrationTypeService.getDecayCalibrationTypes()
    ])
      .toPromise()
      .catch(error => {
        console.error(error);
        return null;
      });

    this.gotEstimatesFromDatabase = false;
    let selectedStorage: IDecayCalibration = null;
    this.estimatesFetchedFromLocalStorage = false;
    if (results) {
      const [dbStorage, localStorage, acquisitions, lastImportDate, productReaggregationHistory, observationLimit] = results;

      this.lastImportDate = lastImportDate;
      this.observationLimit = observationLimit ? +observationLimit.SettingValue : 180;
      this.productReaggregationHistory = productReaggregationHistory as IReaggregationHistoryItem;

      if (localStorage) {
        if (dbStorage) {
          const selectedDate = new Date(dbStorage.DateTimeModified || dbStorage.DateTimeCreated);
          // const offSet = selectedDate.getTimezoneOffset();
          const offSetDate = new Date(selectedDate.setHours(selectedDate.getHours() - selectedDate.getTimezoneOffset() / 60));
          // Perform data comparison
          if (offSetDate >= new Date(localStorage.storeDate)) {
            // Choose DB Storage
            selectedStorage = clone(dbStorage);
            this.clearLocalStorageForProduct(productId);
            this.gotEstimatesFromDatabase = true;
            this.estimatesFetchedFromLocalStorage = false;
          } else {
            // Choose Local Storage
            const InvalidIds = [];
            localStorage.payload.ExcludeAcquisitionIds.forEach(localStorageId => {
              const index = acquisitions.findIndex(acqId => acqId.AcquisitionId === localStorageId);
              if (index === -1) {
                InvalidIds.push(localStorageId);
              }
            });
            localStorage.payload.ExcludeAcquisitionIds = localStorage.payload.ExcludeAcquisitionIds.filter(
              acqId => !InvalidIds.includes(acqId)
            );

            if (localStorage.payload.DecayEstimateSetting) {
              localStorage.payload.DecayEstimateSetting.forEach(
                estimate => (estimate.ExcludeAcquisitionIds = localStorage.payload.ExcludeAcquisitionIds)
              );
            }

            selectedStorage = clone(localStorage.payload);
            this.gotEstimatesFromDatabase = false;
            this.estimatesFetchedFromLocalStorage = true;
          }
        } else {
          // Return local storage
          selectedStorage = clone(localStorage.payload);
          this.gotEstimatesFromDatabase = false;
          this.estimatesFetchedFromLocalStorage = true;
        }
      } else if (dbStorage) {
        // Return DB Storage
        selectedStorage = clone(dbStorage);
        this.clearLocalStorageForProduct(productId);
        this.gotEstimatesFromDatabase = true;
        this.estimatesFetchedFromLocalStorage = false;
      }
    }

    if (!selectedStorage) {
      selectedStorage = null;
    }

    this.loadCalibrationData(selectedStorage);

    return of(selectedStorage).toPromise();
  }

  /**
   * Takes an array of estimate results and adds them to the store
   * @param estimates an array of estimates results
   */
  public setEstimates(estimates: IDecayEstimate[]) {
    this.setState({ estimates }, StoreActions.SetEstimates);
  }

  /**
   * Sets the is local storage pristine to false
   */
  public storeInLocalStorage(estimateResults: IDecayEstimate[], productId: number): void {
    // Removing the empty estimate
    const filteredEstimateResults = estimateResults.filter(est => est.Id !== '');
    const firstEstimateWithCalibrationType = estimateResults.find(e => e.DecayCalibrationType);

    const decayCalibrationType = firstEstimateWithCalibrationType ? firstEstimateWithCalibrationType.DecayCalibrationType : 1;

    // We don't want to save anything if there are no estimates to save
    this.decayService.canDeactivate = filteredEstimateResults.length === 0;
    const payload: IDecayCalibration = {
      DateTimeCreated: null,
      DateTimeModified: null,
      DecayCalibrationId: -1,
      ExcludeAcquisitionIds: this.decayService.excludeAcquisitionIds,
      FormId: 'LocalStorage',
      ProductId: productId,
      RecordStatus: null,
      StartDate: this.decayService.startVintage,
      UserIdCreated: -1,
      UserIdModified: -1,
      CalibrationId: -1,
      ChangeLogs: [],
      DecayEstimateSetting: clone(filteredEstimateResults),
      DecayCalibrationType: decayCalibrationType
    };
    this.localStorageService.storeInLocalStorage(payload, LocalStorageKeys.Decays, productId);
  }

  public fetchEstimateFromLocalStorage(productId): ILocalStorage<IDecayEstimate[]> {
    return this.localStorageService.fetchFromLocalStorage<IDecayEstimate[]>(LocalStorageKeys.Decays, productId);
  }

  /**
   * * Removes an item from the decays local storage based on a specific product ID;
   * * Set the is local storage pristine to true
   */
  public clearLocalStorageForProduct(productId: number): void {
    this.decayService.canDeactivate = true;
    this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Decays, productId);
  }

  /**
   * Checks for a global change across the estimates
   * Checks if estimate was created before last import date
   * Checks if estimate was created before last reaggreation date
   * Checks if the acquisitions matches the acquisitions stored inside the decay balance service
   * Checks if the startVintage in the estimate matches the start vintage inside the decayBalanceService
   *
   * Will set the NeedsReRunFlag to true if a change is needed
   *
   * @returns globalChange if true estimates need to be rerun
   */
  public checkForGlobalChangeInEstimate(): boolean {
    let globalChange = false;

    const currentlySelectedAcquisitions = clone(this.decayService.excludeAcquisitionIds).sort();
    const currentlySelectedAcquisitionsStr = JSON.stringify(currentlySelectedAcquisitions);

    (this.estimates || []).forEach(est => {
      if (est.Id) {
        const acquisitionsInEstimate = est.ExcludeAcquisitionIds !== null ? est.ExcludeAcquisitionIds.sort() : [];
        const currentlySelectedStartVintage = new Date(`${this.decayService.startVintage.substr(0, 19)}.000Z`).toJSON().substr(0, 7);
        const estimateStartVintage = est.StartDate
          ? new Date(`${est.StartDate.substr(0, 19)}.000Z`).toJSON().substr(0, 7)
          : new Date('01-01-1700');

        const estimateTime = new Date(est.DateTimeCreated);
        const reaggregationEndDate = new Date(this.productReaggregationHistory.ReaggregationEndDate);

        if (
          this.decayService.selectedDecayCalibrationType.Id !== est.DecayCalibrationType ||
          estimateTime < reaggregationEndDate ||
          estimateTime < this.lastImportDate ||
          JSON.stringify(acquisitionsInEstimate) !== currentlySelectedAcquisitionsStr ||
          estimateStartVintage !== currentlySelectedStartVintage ||
          est.Curtailment > this.observationLimit ||
          est.EndObservation > this.observationLimit
        ) {
          est.NeedsReRun = true;
          globalChange = true;
        } else {
          est.NeedsReRun = false;
        }
      }
    });

    this.globalChange = globalChange;
    return globalChange;
  }

  private sortEstimates(estimates: IDecayEstimate[]) {
    const estimateInd = estimates.findIndex(est => est.IsChosen);
    if (estimateInd >= 0) {
      const chosenEstimate = estimates.splice(estimateInd, 1);
      estimates.unshift(chosenEstimate[0]);
      return estimates;
    } else {
      if (estimates.length > 0) {
        estimates[0].IsChosen = true;
        this.selectedEstimateIndex = 0;
      }
      return estimates;
    }
  }

  private setEstimatesIds(estimates: IDecayEstimate[]) {
    estimates.forEach((estimate: IDecayEstimate) => {
      if (!estimate.Id) {
        estimate.Id = uuidv4();
      }
    });
    return estimates;
  }

  private setEstimatesNames(estimates: IDecayEstimate[]) {
    estimates.forEach((estimate, index) => {
      if (estimate.Id) {
        estimate.Name = `Estimate ${index + 1}`;
      } else {
        estimate.Name = `New Estimate`;
        estimate.Colour = '#AAAAAA';
      }
    });
    return estimates;
  }

  private setNewEstimate(estimates: IDecayEstimate[]) {
    // Ensuring we have an empty estimate
    const newEstimate = estimates.find(e => !e.Id);
    if (!newEstimate) {
      const emptyEst: IDecayEstimate = {
        ...EMPTY_ESTIMATE,
        StartDate: this.decayService.startVintage,
        ExcludeAcquisitionIds: this.decayService.excludeAcquisitionIds,
        EndObservation: this.decayService.actualObservations,
        Curtailment: this.decayService.maxObservations,
        IsValid: true
      };

      estimates.push(emptyEst);
    }
    return estimates;
  }

  /**
   * Loads a decay calibration and populates all fields
   */
  private loadCalibrationData(calibration: IDecayCalibration): void {
    this.selectedDecayCalibration = calibration;

    if (calibration) {
      const estimateResults = this.cleanupEstimates(calibration.DecayEstimateSetting || []);

      this.decayService.excludeAcquisitionIds = calibration.ExcludeAcquisitionIds || [];
      this.decayService.startVintage = calibration.StartDate;
      this.decayService.setSelectedDecayCalibrationType(calibration.DecayCalibrationType);
      // Only done for db storage other types of estimate could be saved as in valid
      if (!this.estimatesFetchedFromLocalStorage) {
        estimateResults.forEach(est => (est.IsValid = true));
      }
      this.estimates = estimateResults;
      this.setState({ estimateResults }, StoreActions.GetSavedEstimates);
    } else {
      // Loading an empty calibration
      const estimateResults = this.cleanupEstimates([]);

      this.decayService.excludeAcquisitionIds = [];
      estimateResults.forEach(est => (est.IsValid = true));
      this.estimates = estimateResults;
      this.setState({ estimateResults }, StoreActions.GetSavedEstimates);
      this.selectedEstimateIndex = 0;
    }
  }
}
